import { useState } from "react";

import { StatusCodes } from "http-status-codes";

import { MutationException } from "@eisox/api";
import { type Module } from "@eisox/api/core-api";
import { ActionButton, Heading, Modal, RadioGroup, Typography } from "@eisox/design-system";
import { ArrowRightIcon, BracesIcon } from "@eisox/icons";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { moduleApi } from "~/api";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

import { BOILER_ROOM_MODULE_TYPES, concatModuleNames } from "../helpers";

const NAME = "InitDataDialog";

interface InitDataDialogProps extends Modal.RootProps {
  buildingId: string;
  /** Set when opened from a module page: keeps the user there instead of navigating to the CTAs. */
  moduleId?: string;
  children?: React.ReactNode;
}

/** Re-initializes the BMS data of a gateway's modules ("init data by BMS module"), like the legacy socket flow. */
const InitDataDialog: React.FC<InitDataDialogProps> = ({
  buildingId,
  moduleId,
  open: openProp,
  // oxlint-disable-next-line typescript/unbound-method
  onOpenChange,
  defaultOpen,
  children,
}) => {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen ?? false,
  });
  const [selectedGatewayId, setSelectedGatewayId] = useState<string>();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { gateways, modules, boilerRooms, airHandlerUnits } = useBuildingContext(NAME);

  const bmsModules = modules.filter(module => BOILER_ROOM_MODULE_TYPES.includes(module.type));
  // Reset re-initializes every BMS module on the box, not just the one being debugged.
  const targets = bmsModules.filter(module => module.gatewayId === selectedGatewayId);

  const { mutate, isPending } = useMutation({
    meta: { toast: { success: m.reset_variables_success() } },
    mutationFn: async (moduleIds: string[]) => {
      const result = await moduleApi.reset(buildingId, { moduleIds });
      const acknowledged = new Set(result.moduleIdsOk);
      const unacknowledged = moduleIds.filter(id => !acknowledged.has(id));
      // Surface a partial reset (some modules never acknowledged) instead of reporting success.
      if (unacknowledged.length > 0) {
        throw new MutationException(
          { method: "post", url: "/buildings/{buildingId}/modules/reset", status: StatusCodes.GATEWAY_TIMEOUT },
          m.reset_variables_partial_error({ count: unacknowledged.length }),
        );
      }
      return result;
    },
    onSuccess: async () => {
      handleOpenChange(false);
      // Opened from a module page: stay there, and refresh its variables if its own box was reset.
      if (moduleId) {
        if (targets.some(module => module.id === moduleId))
          await queryClient.invalidateQueries(moduleApi.queries.rawData(buildingId, moduleId));
        return;
      }
      const airHandlerUnitId = airHandlerUnits[0]?.id;
      if (airHandlerUnitId) {
        await navigate({
          to: "/buildings/$buildingId/air-handler-units/$airHandlerUnitId",
          params: { buildingId, airHandlerUnitId },
        });
      }
    },
  });

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    setSelectedGatewayId(undefined);
  };

  const handleInitModules = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (targets.length > 0) mutate(targets.map(module => module.id));
  };

  const moduleLabel = (module: Module): string =>
    m.reset_variables_on({
      bn: concatModuleNames(module.id, boilerRooms, airHandlerUnits) ?? "-",
      ip: module.url ?? (module.bacnetDeviceId != null ? String(module.bacnetDeviceId) : "-"),
    });

  const gatewayLabel = (gatewayId: string): string => {
    const gateway = gateways.find(candidate => candidate.id === gatewayId);
    const moduleNames = bmsModules
      .filter(module => module.gatewayId === gatewayId)
      .map(moduleLabel)
      .join(", ");
    return `${gateway?.name ?? "-"} (${moduleNames})`;
  };

  const gatewaysWithModules = gateways.filter(gateway => bmsModules.some(module => module.gatewayId === gateway.id));

  return (
    <Modal.Root open={open} onOpenChange={handleOpenChange}>
      {children && <Modal.Trigger asChild>{children}</Modal.Trigger>}
      <Modal.Content className="w-[min(40rem,calc(100dvw-40px))] overflow-y-auto! text-base">
        <Modal.Header title={m.reset_variables_title()} subtitle={m.reset_variables_subtitle()} icon={<BracesIcon />}>
          <Modal.Close asChild>
            <ActionButton variant="cancel">{m.cancel()}</ActionButton>
          </Modal.Close>
          <ActionButton disabled={!selectedGatewayId || isPending} onClick={handleInitModules}>
            {m.reset_variables_init()}
            <ArrowRightIcon />
          </ActionButton>
        </Modal.Header>
        <p className="mb-5 font-normal italic">{m.reset_variables_note()}</p>
        <Heading as="h4" className="mb-2.5!">
          {m.gateway()}
        </Heading>
        <RadioGroup.Root orientation="vertical" value={selectedGatewayId} onValueChange={setSelectedGatewayId}>
          {gatewaysWithModules.map(gateway => (
            <RadioGroup.Item key={gateway.id} value={gateway.id} className="min-w-0 items-start">
              <Typography className="min-w-0 whitespace-normal">{gatewayLabel(gateway.id)}</Typography>
            </RadioGroup.Item>
          ))}
        </RadioGroup.Root>
      </Modal.Content>
    </Modal.Root>
  );
};

InitDataDialog.displayName = NAME;

export { InitDataDialog };
export type { InitDataDialogProps };
